import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bot,
  Play,
  Square,
  Trash2,
  Loader2,
  Download,
  FileText,
  Folder,
  ChevronRight,
  Wrench,
  Brain,
  Terminal,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  RefreshCw,
  RotateCcw,
  Users,
  User,
  Share2,
  Copy,
  Eye,
  ExternalLink,
  Globe,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const STATUS_STYLES = {
  pending: { color: "bg-zinc-200 text-zinc-700", icon: Clock, label: "Pending" },
  queued: { color: "bg-amber-100 text-amber-800", icon: Clock, label: "Queued" },
  running: { color: "bg-blue-100 text-blue-800", icon: Loader2, label: "Running" },
  completed: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Completed" },
  failed: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Failed" },
  cancelled: { color: "bg-zinc-200 text-zinc-700", icon: XCircle, label: "Cancelled" },
};

const EVENT_ICONS = {
  system: { icon: Sparkles, color: "text-zinc-500" },
  step_start: { icon: ChevronRight, color: "text-blue-500" },
  agent_message: { icon: Brain, color: "text-violet-600" },
  tool_result: { icon: Wrench, color: "text-emerald-600" },
  think: { icon: Brain, color: "text-amber-600" },
  done: { icon: CheckCircle2, color: "text-emerald-600" },
  error: { icon: XCircle, color: "text-red-600" },
  warning: { icon: XCircle, color: "text-amber-600" },
  finished: { icon: CheckCircle2, color: "text-emerald-700" },
  ping: { icon: RefreshCw, color: "text-zinc-300" },
  phase_start: { icon: Users, color: "text-violet-700" },
  plan_ready: { icon: Sparkles, color: "text-violet-700" },
  review_failed: { icon: XCircle, color: "text-amber-700" },
  deliverable_gate: { icon: XCircle, color: "text-amber-700" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = cfg.icon;
  const spin = status === "running";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}
      data-testid={`agent-status-${status}`}
    >
      <Icon className={`w-3 h-3 ${spin ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

function LogLine({ evt }) {
  if (evt.type === "ping") return null;
  const cfg = EVENT_ICONS[evt.type] || EVENT_ICONS.system;
  const Icon = cfg.icon;
  const time = evt.ts ? new Date(evt.ts).toLocaleTimeString() : "";

  let body = null;
  if (evt.type === "agent_message") {
    body = (
      <pre className="text-xs whitespace-pre-wrap break-words font-mono text-zinc-700 mt-1 max-h-40 overflow-y-auto">
        {evt.content}
      </pre>
    );
  } else if (evt.type === "tool_result") {
    const tool = evt.tool || "tool";
    const ok = evt.result?.ok;
    return (
      <div className="flex gap-2 py-1.5 border-b border-zinc-100 last:border-0">
        <Wrench className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${ok ? "text-emerald-600" : "text-red-600"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono font-medium text-zinc-900">{tool}</span>
            <span className={ok ? "text-emerald-700" : "text-red-700"}>
              {ok ? "ok" : "failed"}
            </span>
            {evt.args?.path && <span className="font-mono text-zinc-500 truncate">→ {evt.args.path}</span>}
            {evt.args?.command && (
              <span className="font-mono text-zinc-500 truncate">$ {evt.args.command}</span>
            )}
            {typeof evt.result?.exit_code === "number" && (
              <span className="text-zinc-500">exit:{evt.result.exit_code}</span>
            )}
            <span className="ml-auto text-zinc-400">{time}</span>
          </div>
        </div>
      </div>
    );
  } else if (evt.type === "step_start") {
    body = (
      <span className="text-xs text-zinc-500">
        Step {evt.step} · role: {evt.role} · model: <code className="font-mono">{evt.model}</code>
        {evt.phase && evt.phase !== "single" && (
          <span className="ml-1 text-violet-600">[{evt.phase}]</span>
        )}
      </span>
    );
  } else if (evt.type === "phase_start") {
    body = (
      <span className="text-xs font-semibold text-violet-700">
        {evt.phase?.toUpperCase()} phase started · model: <code className="font-mono">{evt.model}</code>
      </span>
    );
  } else if (evt.type === "plan_ready") {
    body = (
      <span className="text-xs text-violet-700">
        Plan ready · {evt.steps} steps · deliverables: {(evt.deliverables || []).slice(0, 3).join(", ")}
      </span>
    );
  } else if (evt.type === "review_failed") {
    body = (
      <pre className="text-xs whitespace-pre-wrap text-amber-800 mt-1">{evt.feedback}</pre>
    );
  } else if (evt.type === "deliverable_gate") {
    body = (
      <span className="text-xs text-amber-800">
        ⚠️ Builder called <code>done</code> but missing files: {(evt.missing || []).join(", ")}
        {" "}— forcing rework
      </span>
    );
  } else if (evt.type === "think") {
    body = <pre className="text-xs italic text-amber-800 mt-1 whitespace-pre-wrap">{evt.thought}</pre>;
  } else if (evt.type === "finished") {
    body = (
      <span className="text-xs">
        <span className="font-medium">{evt.status}</span> · steps: {evt.steps} · tokens:{" "}
        {evt.tokens?.total_tokens ?? evt.tokens}
      </span>
    );
  } else {
    body = <span className="text-xs text-zinc-700">{evt.message || evt.summary || ""}</span>;
  }

  return (
    <div className="flex gap-2 py-1.5 border-b border-zinc-100 last:border-0">
      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-zinc-900 capitalize">{evt.type.replace("_", " ")}</span>
          <span className="ml-auto text-zinc-400">{time}</span>
        </div>
        {body}
      </div>
    </div>
  );
}

function FilesPanel({ api, jobId, refreshKey }) {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  const loadFiles = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${api}/v1/agents/jobs/${jobId}/files`);
      setFiles(res.data.files || []);
    } catch (err) {
      // job may not have a workspace yet
      setFiles([]);
    }
  }, [api, jobId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles, refreshKey]);

  const openFile = async (path) => {
    setSelected(path);
    setLoading(true);
    try {
      const res = await axios.get(`${api}/v1/agents/jobs/${jobId}/files/raw`, {
        params: { path },
      });
      setContent(res.data.content || "");
    } catch (err) {
      toast.error("Failed to read file");
      setContent("");
    } finally {
      setLoading(false);
    }
  };

  const downloadZip = () => {
    const token = localStorage.getItem("llm_hub_token");
    const url = `${api}/v1/agents/jobs/${jobId}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `agent-${jobId}.zip`;
        link.click();
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-[400px]">
      <Card className="md:col-span-1 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700">
            <Folder className="w-3.5 h-3.5" />
            Files ({files.length})
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={loadFiles}
                    data-testid="agent-refresh-files-btn">
              <RefreshCw className="w-3 h-3" />
            </Button>
            {files.length > 0 && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={downloadZip}
                      data-testid="agent-download-zip-btn">
                <Download className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="h-[360px]">
          {files.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500">No files yet.</div>
          ) : (
            files.map((f) => (
              <button
                key={f.path}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono hover:bg-zinc-50 truncate ${
                  selected === f.path ? "bg-zinc-100 text-zinc-900" : "text-zinc-700"
                }`}
                onClick={() => openFile(f.path)}
                data-testid={`agent-file-${f.path.replace(/[^a-z0-9]/gi, "-")}`}
              >
                <FileText className="w-3 h-3 inline mr-1.5" />
                {f.path}
              </button>
            ))
          )}
        </ScrollArea>
      </Card>
      <Card className="md:col-span-2 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="text-xs font-medium text-zinc-700 truncate">
            {selected || "Select a file to preview"}
          </div>
        </div>
        <ScrollArea className="h-[360px]">
          {loading ? (
            <div className="p-4 text-center text-xs text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          ) : (
            <pre className="text-xs font-mono p-3 whitespace-pre-wrap break-words text-zinc-800">
              {content || "—"}
            </pre>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}

export default function AgentsPage({ api }) {
  const [jobs, setJobs] = useState([]);
  const [queueStatus, setQueueStatus] = useState({});
  const [taskInput, setTaskInput] = useState("");
  const [maxSteps, setMaxSteps] = useState(30);
  const [numAgents, setNumAgents] = useState(3);
  const [mode, setMode] = useState("single");
  const [creating, setCreating] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [selectedMcpIds, setSelectedMcpIds] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const wsRef = useRef(null);
  const logEndRef = useRef(null);
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const token = typeof window !== "undefined" ? localStorage.getItem("llm_hub_token") : null;

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/v1/agents/jobs`);
      setJobs(res.data.items || []);
      setQueueStatus(res.data.queue || {});
    } catch (err) {
      toast.error("Failed to load jobs");
    }
  }, [api]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/v1/agents/health`);
      setHealth(res.data);
    } catch {
      setHealth({ local_llm: { ok: false, error: "Unreachable" } });
    }
  }, [api]);

  const fetchMcpServers = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/v1/mcp/servers`);
      setMcpServers((res.data.items || []).filter((s) => s.enabled));
    } catch {
      setMcpServers([]);
    }
  }, [api]);

  useEffect(() => {
    fetchJobs();
    fetchHealth();
    fetchMcpServers();
    const i = setInterval(fetchJobs, 5000);
    return () => clearInterval(i);
  }, [fetchJobs, fetchHealth, fetchMcpServers]);

  // Auto-scroll logs to bottom
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [logs]);

  // Open WebSocket when a job is selected
  useEffect(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (!selectedJob) {
      setLogs([]);
      return;
    }

    // Load full job (with logs) first
    axios.get(`${api}/v1/agents/jobs/${selectedJob.id}`).then((res) => {
      setLogs(res.data.logs || []);
    });

    // Open WS for live updates
    const token = localStorage.getItem("llm_hub_token");
    const wsUrl = api.replace(/^http/, "ws") + `/v1/agents/jobs/${selectedJob.id}/stream?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === "ping") return;
        setLogs((prev) => [...prev, evt]);
        if (evt.type === "tool_result" || evt.type === "finished") {
          setFilesRefreshKey((k) => k + 1);
        }
        if (evt.type === "finished") {
          fetchJobs();
        }
      } catch {}
    };
    ws.onclose = () => {
      // refresh files & job list once stream closes
      setFilesRefreshKey((k) => k + 1);
      fetchJobs();
    };

    return () => {
      ws.close();
    };
  }, [selectedJob?.id, api, fetchJobs]);

  const handleCreate = async () => {
    if (!taskInput.trim() || taskInput.trim().length < 5) {
      toast.error("Task must be at least 5 characters");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${api}/v1/agents/jobs`, {
        task: taskInput.trim(),
        max_steps: parseInt(maxSteps) || 30,
        num_agents: parseInt(numAgents) || 3,
        mode,
        mcp_server_ids: selectedMcpIds,
      });
      toast.success(`Job queued (position #${res.data.queue_position})`);
      setTaskInput("");
      await fetchJobs();
      setSelectedJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.post(`${api}/v1/agents/jobs/${jobId}/cancel`);
      toast.info("Cancel signal sent");
      await fetchJobs();
    } catch (err) {
      toast.error("Cancel failed");
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Delete this job and its workspace?")) return;
    try {
      await axios.delete(`${api}/v1/agents/jobs/${jobId}`);
      if (selectedJob?.id === jobId) setSelectedJob(null);
      toast.success("Deleted");
      await fetchJobs();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleResume = async (jobId) => {
    try {
      const res = await axios.post(`${api}/v1/agents/jobs/${jobId}/resume`);
      toast.success(`Resumed (queue #${res.data.queue_position})`);
      await fetchJobs();
      setSelectedJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resume failed");
    }
  };

  const handleShare = async (jobId, enabled, showLogs = true) => {
    try {
      const res = await axios.post(`${api}/v1/agents/jobs/${jobId}/share`, {
        enabled,
        show_logs: showLogs,
      });
      if (enabled) {
        const url = `${window.location.origin}/agent-build/${res.data.share_token}`;
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Public link copied to clipboard");
        } catch {
          toast.success("Public link enabled");
        }
      } else {
        toast.info("Sharing disabled");
      }
      // refresh selected
      const updated = await axios.get(`${api}/v1/agents/jobs/${jobId}`);
      setSelectedJob({ ...updated.data, logs: undefined });
      await fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Share failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Bot className="w-7 h-7 text-violet-600" />
            Autonomous Agents
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Ruflo-inspired swarm orchestration on local LLMs (Gemma 4 + Qwen Coder).
            Describe a coding task — the agent plans, codes, tests, and ships.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {health && (
            <Badge
              variant="outline"
              className={
                health.local_llm?.ok
                  ? "border-emerald-300 text-emerald-700"
                  : "border-red-300 text-red-700"
              }
              data-testid="agent-health-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5" />
              Local LLM {health.local_llm?.ok ? "online" : "offline"}
            </Badge>
          )}
          {queueStatus.queue_size !== undefined && queueStatus.queue_size > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              Queue: {queueStatus.queue_size}
            </Badge>
          )}
        </div>
      </div>

      {/* Create new job */}
      <Card data-testid="agent-create-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            New Autonomous Task
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            placeholder="e.g. Build a Python Flask API with a /todos CRUD endpoint, persist to SQLite, write 3 pytest tests, and run them."
            className="min-h-[100px] font-mono text-sm"
            disabled={creating}
            data-testid="agent-task-input"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 sm:col-span-2">
              <Label className="text-xs">Mode</Label>
              <div className="flex gap-1.5 mt-1" data-testid="agent-mode-selector">
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  disabled={creating}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
                    mode === "single"
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                  data-testid="agent-mode-single-btn"
                >
                  <User className="w-3.5 h-3.5" />
                  Single agent
                </button>
                <button
                  type="button"
                  onClick={() => setMode("swarm")}
                  disabled={creating}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
                    mode === "swarm"
                      ? "bg-violet-600 text-white border-violet-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                  data-testid="agent-mode-swarm-btn"
                >
                  <Users className="w-3.5 h-3.5" />
                  Swarm (plan→build→review)
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Max steps</Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={maxSteps}
                onChange={(e) => setMaxSteps(e.target.value)}
                disabled={creating}
                data-testid="agent-max-steps-input"
              />
            </div>
            <div>
              <Label className="text-xs">Num agents (1-15)</Label>
              <Input
                type="number"
                min={1}
                max={15}
                value={numAgents}
                onChange={(e) => setNumAgents(e.target.value)}
                disabled={creating}
                data-testid="agent-num-agents-input"
              />
            </div>
            <div className="col-span-2 sm:col-span-4 flex items-end justify-end">
              <Button
                onClick={handleCreate}
                disabled={creating || !taskInput.trim()}
                className="w-full sm:w-auto"
                data-testid="agent-launch-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Launch Agent
              </Button>
            </div>
          </div>
          {mcpServers.length > 0 && (
            <div data-testid="agent-mcp-picker">
              <Label className="text-xs">MCP Servers (attach extra tools to this job)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {mcpServers.map((s) => {
                  const active = selectedMcpIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() =>
                        setSelectedMcpIds((cur) =>
                          active ? cur.filter((x) => x !== s.id) : [...cur, s.id],
                        )
                      }
                      disabled={creating}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                      }`}
                      data-testid={`agent-mcp-${s.slug}`}
                    >
                      {active ? "✓ " : ""}
                      {s.slug}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-pane: jobs list + selected job detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Jobs list */}
        <Card className="lg:col-span-1" data-testid="agent-jobs-list">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Jobs</CardTitle>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchJobs}
                      data-testid="agent-refresh-jobs-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[460px]">
              {jobs.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-500">
                  No jobs yet. Launch one above.
                </div>
              ) : (
                jobs.map((j) => (
                  <button
                    key={j.id}
                    className={`block w-full text-left px-3 py-2 border-b last:border-0 hover:bg-zinc-50 ${
                      selectedJob?.id === j.id ? "bg-zinc-50" : ""
                    }`}
                    onClick={() => setSelectedJob(j)}
                    data-testid={`agent-job-item-${j.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <code className="text-xs font-mono text-zinc-500 truncate">{j.id}</code>
                      <StatusBadge status={j.status} />
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                      {j.mode === "swarm" ? (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-violet-200 text-violet-700">
                          <Users className="w-2.5 h-2.5 mr-1" />swarm
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                          <User className="w-2.5 h-2.5 mr-1" />single
                        </Badge>
                      )}
                      {j.resumed && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-200 text-amber-700">
                          <RotateCcw className="w-2.5 h-2.5 mr-1" />resumed
                        </Badge>
                      )}
                      {j.preview_running && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-200 text-emerald-700"
                               data-testid={`agent-job-preview-live-${j.id}`}>
                          <Globe className="w-2.5 h-2.5 mr-1" />live
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-700 line-clamp-2">{j.task}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-400">
                      <span>steps: {j.steps_taken}/{j.max_steps}</span>
                      <span>·</span>
                      <span>{new Date(j.created_at).toLocaleString()}</span>
                    </div>
                  </button>
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Selected job detail */}
        <Card className="lg:col-span-2" data-testid="agent-job-detail">
          {!selectedJob ? (
            <CardContent className="h-[460px] flex items-center justify-center text-sm text-zinc-500">
              Select a job to view its live logs and workspace.
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-xs font-mono text-zinc-500">{selectedJob.id}</code>
                      <StatusBadge status={selectedJob.status} />
                    </div>
                    <p className="text-sm text-zinc-700 line-clamp-2">{selectedJob.task}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {(selectedJob.status === "running" ||
                      selectedJob.status === "queued" ||
                      selectedJob.status === "pending") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(selectedJob.id)}
                        data-testid="agent-cancel-btn"
                      >
                        <Square className="w-3.5 h-3.5" />
                        Cancel
                      </Button>
                    )}
                    {(selectedJob.status === "failed" ||
                      selectedJob.status === "cancelled") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResume(selectedJob.id)}
                        data-testid="agent-resume-btn"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Resume
                      </Button>
                    )}
                    {selectedJob.status === "completed" && (
                      <Button
                        variant={selectedJob.share_enabled ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleShare(selectedJob.id, !selectedJob.share_enabled)}
                        data-testid="agent-share-btn"
                        className={selectedJob.share_enabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        {selectedJob.share_enabled ? "Public" : "Share"}
                      </Button>
                    )}
                    {selectedJob.preview_running && selectedJob.preview_url && (
                      <Button
                        variant="default"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          const t = encodeURIComponent(token || "");
                          const url = `${selectedJob.preview_url}?token=${t}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        data-testid="agent-open-preview-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open Preview
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(selectedJob.id)}
                      data-testid="agent-delete-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedJob.share_enabled && selectedJob.share_token && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200">
                    <Share2 className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                    <span className="text-xs text-emerald-800 font-medium flex-shrink-0">
                      Public:
                    </span>
                    <code className="text-xs text-emerald-900 font-mono truncate flex-1">
                      {window.location.origin}/agent-build/{selectedJob.share_token}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        const url = `${window.location.origin}/agent-build/${selectedJob.share_token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Copied");
                      }}
                      data-testid="agent-share-copy-btn"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => window.open(`/agent-build/${selectedJob.share_token}`, "_blank")}
                      data-testid="agent-share-open-btn"
                    >
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {selectedJob.preview_running && selectedJob.preview_url && (
                  <div data-testid="agent-preview-iframe-section">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700">
                        <Globe className="w-3.5 h-3.5 text-emerald-600" />
                        Live Preview
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-200 text-emerald-700 ml-1">
                          running
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Refresh"
                          onClick={() => {
                            const f = document.getElementById(`preview-iframe-${selectedJob.id}`);
                            if (f) f.src = f.src;
                          }}
                          data-testid="agent-preview-refresh-btn"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Open in new tab"
                          onClick={() => {
                            const t = encodeURIComponent(token || "");
                            window.open(`${selectedJob.preview_url}?token=${t}`, "_blank", "noopener,noreferrer");
                          }}
                          data-testid="agent-preview-newtab-btn"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Card className="overflow-hidden bg-white">
                      <iframe
                        id={`preview-iframe-${selectedJob.id}`}
                        title="Agent app preview"
                        src={`${selectedJob.preview_url}?token=${encodeURIComponent(token || "")}`}
                        className="w-full h-[420px] border-0 bg-white"
                        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
                        data-testid="agent-preview-iframe"
                      />
                    </Card>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700 mb-1.5">
                    <Terminal className="w-3.5 h-3.5" />
                    Live Activity ({logs.length})
                  </div>
                  <Card className="overflow-hidden">
                    <ScrollArea className="h-[260px] px-3">
                      {logs.length === 0 ? (
                        <div className="py-6 text-center text-xs text-zinc-500">
                          {selectedJob.status === "queued"
                            ? "Waiting in queue..."
                            : "No activity yet."}
                        </div>
                      ) : (
                        <>
                          {logs.map((evt, i) => (
                            <LogLine key={i} evt={evt} />
                          ))}
                          <div ref={logEndRef} />
                        </>
                      )}
                    </ScrollArea>
                  </Card>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700 mb-1.5">
                    <Folder className="w-3.5 h-3.5" />
                    Workspace
                  </div>
                  <FilesPanel api={api} jobId={selectedJob.id} refreshKey={filesRefreshKey} />
                </div>

                {selectedJob.summary && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs">Summary</Label>
                      <pre className="text-xs whitespace-pre-wrap text-zinc-700 mt-1 font-mono">
                        {selectedJob.summary}
                      </pre>
                    </div>
                  </>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
